import { Injectable } from '@angular/core';
import { Style, Stroke, Fill, Icon, RegularShape, Text, Circle } from 'ol/style';
import { FeatureStyle } from '../models/style.model';

/**
 * Utils for the OpenLayers styles.
 */
@Injectable({
  providedIn: 'root'
})
export class LayerStylesService {
  /** The global styles of the configuration file. */
  mapStyles: FeatureStyle[] = null;

  constructor() {}

  /**
   * Create an style from a FeatureStyle object.
   * @param style The FeatureStyle object of the style
   * @returns An OpenLayers style
   */

  buildStyleFromFeatureStyle(style: FeatureStyle, featureProperties?: any): Style {
    const fill = this.createStyleFill(style);
    const image = this.createStyleImage(style);
    const stroke = this.createStyleStroke(style);
    const text = style.text ? this.createStyleText(style, featureProperties) : null;

    return new Style({ fill, image, stroke, text });
  }

  buildStyleArrayFromFeatureStyle(style: FeatureStyle, featureProperties?: any): Style[] {
    const mainStyle = this.buildStyleFromFeatureStyle(style, featureProperties);

    if (style.halo) {
      const haloWidth = style.halo.width ?? (style.stroke?.width ?? 2) + 4;
      const haloStyle = new Style({
        stroke: new Stroke({
          color: [style.halo.r, style.halo.g, style.halo.b, style.halo.a],
          width: haloWidth,
          lineCap: 'round',
          lineJoin: 'round'
        })
      });
      return [haloStyle, mainStyle];
    }

    return [mainStyle];
  }

  /**
   * Construct a style from an ID of a style that is present in the configuration.
   * @param idStyleConfiguration String containing the id of the style present in the configuration.
   * @returns The created style or an empty style.
   */
  buildFeatureStyleFromId(idStyleConfiguration: string, featureProperties?: any): Style {
    let configStyle: FeatureStyle[] = [];

    if (this.mapStyles) {
      configStyle = this.mapStyles.filter((s) => s.name === idStyleConfiguration);
    }
    if (configStyle.length > 0) {
      const style = configStyle[0] as FeatureStyle; //take the first
      return this.buildStyleFromFeatureStyle(style, featureProperties);
    } else {
      return new Style();
    }
  }

  /**
   * Create Fill style for polygons.
   * The jsonStyle parameter must have the parameters fill.r, fill.g, fill.b, fill.a.
   * @param jsonStyle Style configuration.
   * @returns Fill style.
   */
  createStyleFill(jsonStyle: FeatureStyle): Fill {
    if (jsonStyle.fill) {
      let fillStyle = jsonStyle.fill;
      if (
        fillStyle.r == undefined ||
        fillStyle.g == undefined ||
        fillStyle.b == undefined ||
        fillStyle.a == undefined
      ) {
        return undefined;
      }
      return new Fill({
        color: [fillStyle.r, fillStyle.g, fillStyle.b, fillStyle.a]
      });
    } else {
      return undefined;
    }
  }

  /**
   * Create a line style.
   * The jsonStyle parameter must have the parameters r, g, b, a.
   * @param jsonStyle style configuration.
   * @returns Stroke style.
   */
  createStyleStroke(jsonStyle: FeatureStyle): Stroke {
    if (jsonStyle.stroke) {
      let strokeStyle = jsonStyle.stroke;
      //Check if it has all the mandatory elements to create a stroke
      if (
        strokeStyle.r == undefined ||
        strokeStyle.g == undefined ||
        strokeStyle.b == undefined ||
        strokeStyle.a == undefined
      ) {
        return undefined;
      }

      return new Stroke({
        color: [strokeStyle.r, strokeStyle.g, strokeStyle.b, strokeStyle.a],
        width: strokeStyle.width ? strokeStyle.width : 1, //if not width is specified, 1 is default
        lineCap: 'round',
        lineJoin: 'round',
        lineDash: strokeStyle.pattern ? strokeStyle.pattern : undefined,
        lineDashOffset: 0
      });
    } else {
      return undefined;
    }
  }

  /**
   * Create an 'image' style.
   * @param jsonStyle Syle configuration.
   * @returns Icon or RegularShape style.
   */
  createStyleImage(jsonStyle: FeatureStyle): Icon | RegularShape {
    if (jsonStyle.image) {
      let imageStyle = jsonStyle.image;
      if (imageStyle.imageType == 'icon') {
        //Check if it has all the mandatory elements to create an icon

        return new Icon({
          src: imageStyle.src,
          scale: imageStyle.scale,
          //imgSize: [jsonStyle.iconWidth, jsonStyle.iconHeight],
          opacity: 1,
          rotation: imageStyle.rotation != undefined ? imageStyle.rotation : 0
        });
      } else {
        return new Circle({
          radius: imageStyle.radius != undefined ? imageStyle.radius : 5,
          fill: this.createStyleFill(imageStyle as FeatureStyle),
          stroke: this.createStyleStroke(imageStyle as FeatureStyle)
        });
      }
    } else {
      return undefined;
    }
  }

  /**
   * Create a line style.
   * @param jsonStyle style configuration.
   * @returns Stroke style.
   */
  createStyleText(jsonStyle: FeatureStyle, featureProperties?: any): Text {
    if (jsonStyle) {
      let textStyle = jsonStyle.text;
      if (textStyle.title == undefined || textStyle.fontSize == undefined || textStyle.fontWeight == undefined) {
        return undefined;
      }

      return new Text({
        text:
          featureProperties && featureProperties[textStyle.title] ? '' + featureProperties[textStyle.title] || '' : '',
        font: `${textStyle.fontWeight} ${textStyle.fontSize}px sans-serif`,
        textAlign: textStyle.alignment ? <CanvasTextAlign>textStyle.alignment : undefined,
        fill: this.createStyleFill(textStyle as any),
        stroke: this.createStyleStroke(textStyle as any)
      });
    } else return undefined;
  }
}
